/**
 * Performance Monitor
 *
 * Real User Monitoring (RUM) for tracking:
 * - Core Web Vitals (LCP, FID, CLS)
 * - API response times
 * - Performance metrics
 * - Error tracking
 */

// Performance metrics storage
const metrics = {
  vitals: {},
  apiTimings: [],
  errors: [],
};

// Cleanup references to prevent memory leaks
let _intervalId = null;
let _observers = [];
let _errorHandlers = [];
let _originalFetch = null;
let _initialized = false;

/**
 * Initialize performance monitoring — idempotent, safe to call once.
 * Returns a cleanup function.
 */
export function initPerformanceMonitoring() {
  if (typeof window === 'undefined' || _initialized) return () => {};
  _initialized = true;

  trackWebVitals();
  trackAPIPerformance();
  trackErrors();

  if (process.env.NODE_ENV === 'development') {
    _intervalId = setInterval(() => {
      console.log('Performance Metrics:', getMetrics());
    }, 30000);
  }

  return cleanup;
}

/**
 * Track Core Web Vitals
 */
function trackWebVitals() {
  const lcpObserver = new PerformanceObserver((entryList) => {
    const entries = entryList.getEntries();
    const lastEntry = entries[entries.length - 1];
    metrics.vitals.lcp = {
      value: lastEntry.renderTime || lastEntry.loadTime,
      timestamp: Date.now(),
    };
    logMetric('LCP', metrics.vitals.lcp.value);
  });
  lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
  _observers.push(lcpObserver);

  const fidObserver = new PerformanceObserver((entryList) => {
    entryList.getEntries().forEach((entry) => {
      metrics.vitals.fid = {
        value: entry.processingStart - entry.startTime,
        timestamp: Date.now(),
      };
      logMetric('FID', metrics.vitals.fid.value);
    });
  });
  fidObserver.observe({ entryTypes: ['first-input'] });
  _observers.push(fidObserver);

  let clsValue = 0;
  const clsObserver = new PerformanceObserver((entryList) => {
    entryList.getEntries().forEach((entry) => {
      if (!entry.hadRecentInput) {
        clsValue += entry.value;
        metrics.vitals.cls = { value: clsValue, timestamp: Date.now() };
        logMetric('CLS', clsValue);
      }
    });
  });
  clsObserver.observe({ entryTypes: ['layout-shift'] });
  _observers.push(clsObserver);

  const fcpObserver = new PerformanceObserver((entryList) => {
    entryList.getEntries().forEach((entry) => {
      if (entry.name === 'first-contentful-paint') {
        metrics.vitals.fcp = { value: entry.startTime, timestamp: Date.now() };
        logMetric('FCP', entry.startTime);
      }
    });
  });
  fcpObserver.observe({ entryTypes: ['paint'] });
  _observers.push(fcpObserver);

  const onLoad = () => {
    setTimeout(() => {
      const perfData = performance.getEntriesByType('navigation')[0];
      if (perfData) {
        const tti = perfData.domInteractive - perfData.fetchStart;
        metrics.vitals.tti = { value: tti, timestamp: Date.now() };
        logMetric('TTI', tti);
      }
    }, 0);
  };
  window.addEventListener('load', onLoad);
  _errorHandlers.push(() => window.removeEventListener('load', onLoad));
}

/**
 * Track API performance — wraps fetch once, restores on cleanup.
 */
function trackAPIPerformance() {
  // Guard: don't double-wrap
  if (_originalFetch) return;
  _originalFetch = window.fetch;

  window.fetch = async function (...args) {
    const startTime = performance.now();
    const url = args[0];

    try {
      const response = await _originalFetch.apply(this, args);
      const duration = performance.now() - startTime;

      metrics.apiTimings.push({
        url: typeof url === 'string' ? url : url.url,
        method: args[1]?.method || 'GET',
        duration,
        status: response.status,
        timestamp: Date.now(),
      });

      if (metrics.apiTimings.length > 100) metrics.apiTimings.shift();

      return response;
    } catch (error) {
      metrics.apiTimings.push({
        url: typeof url === 'string' ? url : url.url,
        method: args[1]?.method || 'GET',
        duration: performance.now() - startTime,
        error: error.message,
        timestamp: Date.now(),
      });
      throw error;
    }
  };
}

/**
 * Track errors
 */
function trackErrors() {
  const onError = (event) => {
    metrics.errors.push({
      type: 'javascript',
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      timestamp: Date.now(),
    });
  };

  const onUnhandledRejection = (event) => {
    metrics.errors.push({
      type: 'promise',
      reason: event.reason?.toString() || 'Unknown error',
      timestamp: Date.now(),
    });
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  _errorHandlers.push(
    () => window.removeEventListener('error', onError),
    () => window.removeEventListener('unhandledrejection', onUnhandledRejection),
  );
}

/**
 * Cleanup all monitoring resources — call on app unmount.
 */
export function cleanup() {
  if (_intervalId !== null) {
    clearInterval(_intervalId);
    _intervalId = null;
  }

  _observers.forEach((obs) => obs.disconnect());
  _observers = [];

  _errorHandlers.forEach((fn) => fn());
  _errorHandlers = [];

  if (_originalFetch) {
    window.fetch = _originalFetch;
    _originalFetch = null;
  }

  _initialized = false;
}

function logMetric(name, value) {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Performance] ${name}: ${value.toFixed(2)}ms`);
  }
}

export function getMetrics() {
  return {
    vitals: { ...metrics.vitals },
    apiTimings: [...metrics.apiTimings],
    errors: [...metrics.errors],
    summary: getPerformanceSummary(),
  };
}

function getPerformanceSummary() {
  const { vitals, apiTimings } = metrics;
  const avgApiTime =
    apiTimings.length > 0
      ? apiTimings.reduce((sum, t) => sum + t.duration, 0) / apiTimings.length
      : 0;

  return {
    lcp: vitals.lcp?.value || null,
    fid: vitals.fid?.value || null,
    cls: vitals.cls?.value || null,
    fcp: vitals.fcp?.value || null,
    tti: vitals.tti?.value || null,
    avgApiTime: avgApiTime.toFixed(2),
    totalApiCalls: apiTimings.length,
    totalErrors: metrics.errors.length,
  };
}

export function measurePerformance(name, fn) {
  const startTime = performance.now();
  const result = fn();
  const duration = performance.now() - startTime;
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Performance] ${name}: ${duration.toFixed(2)}ms`);
  }
  return { result, duration };
}

export async function measureAsyncPerformance(name, fn) {
  const startTime = performance.now();
  const result = await fn();
  const duration = performance.now() - startTime;
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Performance] ${name}: ${duration.toFixed(2)}ms`);
  }
  return { result, duration };
}

export function clearMetrics() {
  metrics.vitals = {};
  metrics.apiTimings = [];
  metrics.errors = [];
}
